/**
 * Typed client for the public storefront API.
 *
 * Endpoints live under /api/v1/storefront/* (anonymous, no cookie
 * required). Mirror of the FastAPI surface in
 * apps/backend/app/api/v1/storefront.py.
 */

const API_BASE = '/api/v1';

export class StorefrontError extends Error {
  status: number;
  detail: string;
  constructor(status: number, detail: string) {
    super(detail);
    this.name = 'StorefrontError';
    this.status = status;
    this.detail = detail;
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set('Accept', 'application/json');
  if (init.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers,
    credentials: 'same-origin',
  });
  if (!res.ok) {
    let detail = res.statusText;
    try {
      const j = await res.json();
      detail = j?.detail ?? JSON.stringify(j);
    } catch {
      // ignore
    }
    throw new StorefrontError(res.status, detail);
  }
  return (await res.json()) as T;
}

// ─── Types (mirror Pydantic models) ────────────────────────────────────

export type InputShape = 'single_chart' | 'two_chart' | 'rectify';

export interface SkuEntry {
  slug: string;
  title_hi: string;
  title_en: string;
  blurb_hi: string;
  blurb_en: string;
  emoji: string;
  price_paise: number;
  price_inr_display: string;
  input_shape: InputShape;
  badge: 'flagship' | 'new' | null;
}

export interface CatalogueResponse {
  razorpay_configured: boolean;
  skus: SkuEntry[];
}

export interface CouponValidateResponse {
  valid: boolean;
  code: string;
  owner_display_name: string;
  discount_paise: number;
  base_price_paise: number;
  final_price_paise: number;
  final_price_inr_display: string;
  is_free: boolean;
  error: string | null;
}

export interface StorefrontBuyer {
  name: string;
  email: string;
  phone?: string | null;
}

export interface CreateOrderRequest {
  report_slug: string;
  buyer: StorefrontBuyer;
  chart_request: Record<string, unknown>;
  coupon_code?: string | null;
  lang: 'hi' | 'en';
}

export interface CreateOrderResponse {
  order_id: number;
  public_token: string;
  final_price_paise: number;
  final_price_inr_display: string;
  is_free: boolean;
  razorpay_order_id: string | null;
  razorpay_key_id: string | null;
  buyer_email: string;
  buyer_name: string;
  report_slug: string;
}

export interface VerifyPaymentRequest {
  order_id: number;
  razorpay_payment_id: string;
  razorpay_signature: string;
}

export interface VerifyPaymentResponse {
  ok: boolean;
  status: string;
  public_token: string | null;
}

export interface OrderStatusResponse {
  public_token: string;
  status: string;
  report_slug: string;
  report_title_hi: string;
  report_title_en: string;
  buyer_email_masked: string;
  final_price_paise: number;
  final_price_inr_display: string;
  created_at: string;
  paid_at: string | null;
  delivered_at: string | null;
}

// ─── API surface ───────────────────────────────────────────────────────

export const storefrontApi = {
  catalogue: () => request<CatalogueResponse>('/storefront/catalogue'),

  validateCoupon: (code: string, report_slug: string) =>
    request<CouponValidateResponse>('/storefront/coupons/validate', {
      method: 'POST',
      body: JSON.stringify({ code, report_slug }),
    }),

  createOrder: (req: CreateOrderRequest) =>
    request<CreateOrderResponse>('/storefront/orders', {
      method: 'POST',
      body: JSON.stringify(req),
    }),

  verifyPayment: (req: VerifyPaymentRequest) =>
    request<VerifyPaymentResponse>('/storefront/orders/verify', {
      method: 'POST',
      body: JSON.stringify(req),
    }),

  orderStatus: (publicToken: string) =>
    request<OrderStatusResponse>(`/storefront/orders/${encodeURIComponent(publicToken)}`),

  resendOrder: (publicToken: string) =>
    request<{ ok: boolean; attempts_remaining: number }>(
      `/storefront/orders/${encodeURIComponent(publicToken)}/resend`,
      { method: 'POST' },
    ),
};
